import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Trophy, Tv, Calendar, Bell, User, ArrowRight, Network } from "lucide-react";
import { fmtTime } from "@/lib/format";

export default function PlayerPortal() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [matches, setMatches] = useState([]);
  const [team, setTeam] = useState(null);
  const [notif, setNotif] = useState({ total: 0, by_category: {} });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const reqs = [
          api.get("/matches"),
          api.get("/notifications/unread-count").catch(() => ({ data: { unread: 0, by_category: {} } })),
        ];
        if (user.team_id) reqs.push(api.get("/teams").catch(() => ({ data: [] })));
        const [m, n, t] = await Promise.all(reqs);
        setMatches(m.data || []);
        setNotif(n.data || { unread: 0, by_category: {} });
        if (t?.data && user.team_id) {
          setTeam((t.data || []).find((x) => x.id === user.team_id) || null);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [user.team_id]);

  const live = useMemo(() => matches.filter((m) => m.status === "live"), [matches]);
  const upcoming = useMemo(
    () => matches.filter((m) => m.status === "scheduled").slice(0, 6),
    [matches]
  );
  const myUpcoming = useMemo(() => {
    if (!user.team_id) return [];
    return matches.filter(
      (m) =>
        m.status !== "finished" &&
        (m.team_a?.id === user.team_id || m.team_b?.id === user.team_id)
    );
  }, [matches, user.team_id]);

  return (
    <div className="p-6 md:p-10 max-w-7xl" data-testid="player-portal">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#7DD3FC]">Player Portal</div>
        <h1 className="font-anton text-5xl md:text-6xl uppercase tracking-tighter text-white mt-1 leading-none">
          Hey,<br/><span className="text-[#7DD3FC]">{(user.name || "Fan").split(" ")[0]}.</span>
        </h1>
        <div className="mt-3 text-zinc-400 text-sm max-w-xl">
          Your season hub — follow live matches, keep an eye on your team, and never miss a notification.
        </div>
      </div>

      {/* Top row: profile + notifications */}
      <div className="grid md:grid-cols-3 gap-4 mb-8">
        <Card className="p-5 bg-[#11241b] border-white/10 md:col-span-2" data-testid="player-identity-card">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-[#7DD3FC]/10 border border-[#7DD3FC]/40 flex items-center justify-center shrink-0">
              <User className="w-7 h-7 text-[#7DD3FC]" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Signed in as</div>
              <div className="font-anton text-2xl uppercase tracking-tight text-white truncate">{user.name}</div>
              <div className="text-xs text-zinc-400 truncate">{user.email}</div>
            </div>
            {team && (
              <div className="text-right shrink-0">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Your Team</div>
                <div
                  className="font-anton text-xl uppercase tracking-tight"
                  style={{ color: team.brand_color || team.primary_color || "#7DD3FC" }}
                >
                  {team.name}
                </div>
              </div>
            )}
          </div>
        </Card>

        <button
          data-testid="player-notif-card"
          onClick={() => nav("/notifications")}
          className="p-5 rounded-xl bg-[#11241b] border border-white/10 hover:border-[#7DD3FC]/40 text-left transition-all"
        >
          <div className="flex items-center gap-3">
            <Bell className="w-5 h-5 text-[#7DD3FC]" />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Unread notifications</div>
              <div className="font-anton text-4xl tracking-tighter text-white leading-none">
                {notif.unread > 99 ? "99+" : notif.unread}
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-zinc-500" />
          </div>
        </button>
      </div>

      {/* Live now */}
      {live.length > 0 && (
        <section className="mb-8" data-testid="player-live-section">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            <h2 className="font-anton uppercase text-2xl tracking-tight text-white">Live Right Now</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {live.map((m) => (
              <button
                key={m.id}
                data-testid={`player-live-${m.id}`}
                onClick={() => nav(`/watch/${m.id}`)}
                className="text-left p-5 rounded-xl bg-[#060d09] border border-white/10 hover:border-[#7DD3FC]/60 transition-all"
              >
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">{m.court} · Period {m.current_period}</div>
                <div className="mt-2 flex items-baseline justify-between gap-3">
                  <div className="font-anton text-2xl uppercase text-white truncate">{m.team_a?.name || "—"}</div>
                  <div className="font-mono-score text-3xl text-white">{m.score_a} · {m.score_b}</div>
                  <div className="font-anton text-2xl uppercase text-white truncate text-right">{m.team_b?.name || "—"}</div>
                </div>
                <div className="mt-3 text-xs text-[#7DD3FC] font-mono-score">{fmtTime(m.match_remaining)} remaining · Tap to watch</div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* My team matches */}
      {myUpcoming.length > 0 && (
        <section className="mb-8" data-testid="player-team-section">
          <div className="flex items-center gap-3 mb-4">
            <Trophy className="w-5 h-5 text-[#F4B400]" />
            <h2 className="font-anton uppercase text-2xl tracking-tight text-white">Your Team&apos;s Fixtures</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {myUpcoming.map((m) => (
              <FixtureRow key={m.id} m={m} onClick={() => nav(`/watch/${m.id}`)} testid={`player-team-fixture-${m.id}`} />
            ))}
          </div>
        </section>
      )}

      {/* Quick tiles */}
      <section className="mb-8" data-testid="player-quicknav">
        <div className="flex items-center gap-3 mb-4">
          <Calendar className="w-5 h-5 text-[#00A86B]" />
          <h2 className="font-anton uppercase text-2xl tracking-tight text-white">Explore The League</h2>
        </div>
        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3">
          <Tile icon={Tv} label="Live Broadcast" onClick={() => nav("/live")} testid="player-tile-live" />
          <Tile icon={Calendar} label="Schedule" onClick={() => nav("/schedule")} testid="player-tile-schedule" />
          <Tile icon={Network} label="Bracket" onClick={() => nav("/bracket")} testid="player-tile-bracket" />
          <Tile icon={Trophy} label="Standings" onClick={() => nav("/standings")} testid="player-tile-standings" />
        </div>
      </section>

      {/* Upcoming */}
      {upcoming.length > 0 && (
        <section className="mb-8" data-testid="player-upcoming-section">
          <div className="flex items-center gap-3 mb-4">
            <Calendar className="w-5 h-5 text-zinc-400" />
            <h2 className="font-anton uppercase text-2xl tracking-tight text-white">Upcoming Matches</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {upcoming.map((m) => (
              <FixtureRow key={m.id} m={m} onClick={() => nav(`/watch/${m.id}`)} testid={`player-upcoming-${m.id}`} />
            ))}
          </div>
        </section>
      )}

      {loading && <div className="text-zinc-500 text-sm">Loading…</div>}
    </div>
  );
}

function Tile({ icon: Icon, label, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className="p-4 rounded-xl bg-[#11241b] border border-white/10 hover:border-[#7DD3FC]/40 text-left transition-all flex items-center gap-3"
    >
      <Icon className="w-5 h-5 text-[#7DD3FC]" />
      <span className="text-sm font-bold uppercase tracking-widest text-white">{label}</span>
    </button>
  );
}

function FixtureRow({ m, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className="p-4 rounded-xl bg-[#11241b] border border-white/10 hover:border-white/30 transition-all text-left"
    >
      <div className="flex items-center justify-between">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">{m.court} · {m.stage}</div>
          <div className="font-anton text-lg uppercase tracking-tight text-white truncate">
            {m.team_a?.name || "TBD"} <span className="text-zinc-500">vs</span> {m.team_b?.name || "TBD"}
          </div>
          {m.scheduled_at && <div className="text-xs text-zinc-500 mt-1">{m.scheduled_at}</div>}
        </div>
        <ArrowRight className="w-4 h-4 text-zinc-500 shrink-0" />
      </div>
    </button>
  );
}
